import { Box, Flex, IconArrowDropDown14, ImageButton, SortButton } from '@gmg/gmg-react-components';
import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Organization } from 'src/viewModels/Organization';
import styled, { css } from 'styled-components';
import useFormatter from '../../customHooks/useFormatter';
import useSort from '../../customHooks/useSort';
import IconCornerDownRight from '../../assets/svg/icon_corner_down_right.svg';
import { SortingOrder, Table, TableContainer, TableContainerCardStyle, Thead, Tr } from '../../shared/TableStyle';
import { groupOrganizationsByHead } from './groupOrganizationsByHead';
import OrganizationActions from './OrganizationActions';

interface OrganizationsListProps {
    orgListData: Array<Organization>;
    filterInput: string;
}

const OrganizationsList: FC<OrganizationsListProps> = (props) => {
    const { t } = useTranslation();
    const { formatDate } = useFormatter();
    const navigate = useNavigate();
    const { getSortFn } = useSort();
    const [sortingStatus, setSortingStatus] = useState<SortingOrder>({ direction: 'ASC', propertyName: 'name' });
    const [expandedHeadId, setExpandedHeadId] = useState<string | null>(null);

    // TODO: error handling

    const handleSortButtonClick = (label: string) => {

        if (label !== sortingStatus.propertyName) {
            setSortingStatus({
                direction: 'ASC',
                propertyName: label,
            });
        } else {
            setSortingStatus({
                direction: sortingStatus.direction === 'ASC' ? 'DESC' : 'ASC',
                propertyName: label,
            });
        }

    };

    const isColumnSorted = (propName: string) => sortingStatus.propertyName === propName ? sortingStatus.direction : undefined;
    const sortFn = getSortFn(sortingStatus.propertyName, sortingStatus.direction);
    const filter = props.filterInput.trim().toLowerCase();

    const groupedOrganizations = groupOrganizationsByHead(props.orgListData);
    const groupedOrganizationsSorted = [...groupedOrganizations].sort((a, b) => sortFn(a.org, b.org));
    const matchesFilter = (o: Organization) =>
        [o.name, o.code, o.city, o.contactEmail].some(value => value.toLowerCase().includes(filter));

    const filteredOrganizations = groupedOrganizationsSorted.filter(({ org, sites }) =>
        [org, ...sites].some(matchesFilter),
    );

    const siteAssignmentCandidates = props.orgListData.map(({ id, name, parentOrgId, isHeadOrganization }) => ({
        id,
        name,
        parentOrgId,
        isHeadOrganization,
    }));


    return (
        <TableContainer>
            <TableContainerCardStyle aria-label='TableContainerCardStyle' className='vertical-scrollbar'>
                <FixedLayoutTable aria-label="List of Organizations">
                    {/* fixed column widths so expanding a head org's sites doesn't reflow the columns */}
                    <colgroup>
                        <col style={{ width: '68px' }} />
                        <col />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '16%' }} />
                        <col style={{ width: 'var(--table-actions-width)' }} />
                    </colgroup>
                    <Thead>
                        <Tr isSelected={false} isHeaderRow>
                            <th aria-hidden="true" />{/* placeholder column that reserves space for the head organization expand arrow */}
                            <th className="px12_600_normal" style={{ paddingLeft: 0 }}>
                                <SortButton
                                    buttonLabel={t(
                                        'OrganizationList.tableLabels.organization_title',
                                        {
                                            defaultValue: 'Organization (total number: {{totalNumber}})',
                                            totalNumber: props.orgListData.length || '0',
                                        },
                                    )}
                                    onClick={() => handleSortButtonClick('name')}
                                    direction={isColumnSorted('name')} />
                            </th>
                            <th className="px12_600_normal">
                                <SortButton
                                    buttonLabel={t('OrganizationList.tableLabels.city_title', 'City')}
                                    onClick={() => handleSortButtonClick('city')}
                                    direction={isColumnSorted('city')} />
                            </th>
                            <th className="px12_600_normal">
                                <SortButton
                                    buttonLabel={t('OrganizationList.tableLabels.country_title', 'Country')}
                                    onClick={() => handleSortButtonClick('country')}
                                    direction={isColumnSorted('country')} />
                            </th>
                            <th className="px12_600_normal">
                                {t('OrganizationList.tableLabels.organizationCode_title', 'Organization Code')}
                            </th>
                            <th className="px12_600_normal">
                                <SortButton
                                    buttonLabel={t('OrganizationList.tableLabels.registrationDate_title', 'Registration date')}
                                    onClick={() => handleSortButtonClick('registration')}
                                    direction={isColumnSorted('registration')} />
                            </th>
                            <th className="px12_600_normal">
                                {t('OrganizationList.tableLabels.contact_title', 'Primary contact')}
                            </th>
                            <th></th>{/* in order to fill the last item's place in the thead 'cos we have an extra td for action button */}
                        </Tr>
                    </Thead>
                    <tbody>
                        {filteredOrganizations.map(({ org, sites }) => {
                            const hasSites = sites.length > 0;
                            const isCurrentOrgExpanded = expandedHeadId === org.id;

                            const toggleExpanded = () => {
                                // Only one head organization is expanded at a time.
                                setExpandedHeadId((prev) => (prev === org.id ? null : org.id));
                            };

                            const expandOrganizationCell = hasSites ? (
                                <ExpandButton
                                    aria-label={t('OrganizationList.toggle_sites_aria', {
                                        defaultValue: 'Toggle sites of {{name}}',
                                        name: org.name,
                                    })}
                                    aria-expanded={isCurrentOrgExpanded}
                                    $isExpanded={isCurrentOrgExpanded}
                                    icon={<IconArrowDropDown14 width={14} height={14} />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpanded();
                                    }}
                                />
                            ) : null;

                            const organizationHeaderCell = (
                                <>
                                    <Flex gap='5px' flexDirection='column'>
                                        <Box>{org.name}</Box>
                                        {hasSites && (
                                            <span className="px10_400_weak">
                                                {t('OrganizationList.sites_count', {
                                                    defaultValue: '{{count}} sites',
                                                    count: sites.length,
                                                })}
                                            </span>
                                        )}
                                    </Flex>
                                </>
                            );

                            return (
                                <Fragment key={org.id}>
                                    <StyledOrganizationRow
                                        $isInExpandedGroup={isCurrentOrgExpanded}
                                        $isExpandedHead={isCurrentOrgExpanded}
                                        onClick={() => navigate(`${org.id}`)}
                                        isClickable
                                        data-testid="row"
                                    >
                                        <td
                                            className="arrow-placeholder"
                                            style={{ cursor: hasSites ? 'pointer' : 'default' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (hasSites) {
                                                    toggleExpanded();
                                                }
                                            }}
                                        >
                                            {expandOrganizationCell}
                                        </td>
                                        <td className="px12_400_normal">{organizationHeaderCell}</td>
                                        <td className="px12_400_normal">{org.city}</td>
                                        <td className="px12_400_normal">{org.country}</td>
                                        <td className="px12_400_normal">{org.code}</td>
                                        <td className="px12_400_normal">{formatDate(org.registration)}</td>
                                        <td className="px12_400_normal">{org.contactEmail}</td>
                                        <td className='actions' onClick={(e) => e.stopPropagation()}>
                                            <OrganizationActions
                                                orgId={org.id}
                                                orgName={org.name}
                                                isSiteOrganization={org.isSiteOrganization}
                                                siteAssignmentCandidates={siteAssignmentCandidates}
                                            />
                                        </td>
                                    </StyledOrganizationRow>
                                    {isCurrentOrgExpanded && [...sites].sort(sortFn).map((site) => (
                                        <StyledOrganizationRow
                                            key={site.id}
                                            $isInExpandedGroup
                                            $isSite
                                            onClick={() => navigate(`${site.id}`)}
                                            isClickable
                                            data-testid="site-row"
                                        >
                                            <td className="arrow-placeholder" />
                                            <td className="px12_400_normal">
                                                <SiteNameCell>
                                                    <SiteIcon><IconCornerDownRight width={16} height={16} /></SiteIcon>
                                                    {site.name}
                                                </SiteNameCell>
                                            </td>
                                            <td className="px12_400_normal">{site.city}</td>
                                            <td className="px12_400_normal">{site.country}</td>
                                            <td className="px12_400_normal">{site.code}</td>
                                            <td className="px12_400_normal">{formatDate(site.registration)}</td>
                                            <td className="px12_400_normal">{site.contactEmail}</td>
                                            <td className='actions' onClick={(e) => e.stopPropagation()}>
                                                <OrganizationActions
                                                    orgId={site.id}
                                                    orgName={site.name}
                                                    isSiteOrganization={site.isSiteOrganization}
                                                    siteAssignmentCandidates={siteAssignmentCandidates}
                                                />
                                            </td>
                                        </StyledOrganizationRow>
                                    ))}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </FixedLayoutTable>
            </TableContainerCardStyle>
        </TableContainer>
    );
};

const FixedLayoutTable = styled(Table)`
    table-layout: fixed;
`;

const ExpandButton = styled(ImageButton)<{ $isExpanded: boolean }>`
    transform: ${(props) => (props.$isExpanded ? 'rotate(0.5turn)' : 'rotate(0)')};
    transition: 0.3s ease transform;
`;
const SiteNameCell = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;
const SiteIcon = styled.span`
    color: ${({ theme }) => theme.colors.greyContrastMediumHigher};
    display: flex;
`;

const StyledOrganizationRow = styled(Tr).attrs({ isSelected: false })<{ $isInExpandedGroup: boolean; $isExpandedHead?: boolean; $isSite?: boolean }>`
    ${(props) =>
        props.$isInExpandedGroup &&
        css`
            &:nth-child(odd),
            &:nth-child(even) {
                background-color: ${props.theme.colors.highlightLight};
            }
        `}

    ${(props) =>
        props.$isExpandedHead &&
        css`
            td {
                border-top: 1px solid ${props.theme.colors.highlight};
                border-bottom: 1px solid ${props.theme.colors.highlight};
            }
        `}

    ${(props) =>
        props.$isSite &&
        css`
            td {
                border-bottom: 1px solid ${props.theme.colors.background};
            }
        `}
`;

export default OrganizationsList;
